import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// graphql
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import GET_NAVBAR_INFO from "graphql/queries/GetNavbarInfo";
import GET_MY_CLASSES from "graphql/queries/GetMyClasses";
import BOOK_COURSE from "graphql/mutations/BookCourse";
import GET_COURSE_DETAILS from "graphql/queries/GetCourseDetails";

// common
import {
  ModalText,
  ModalLayout,
  LargeModalStyling,
  MoreModalButtons,
} from "common/modal";

const { LargeModal, LargeModalCreditInfo } = LargeModalStyling;
const { ConfirmationText, CreditWarning } = ModalText;
const { ModalRow } = ModalLayout;
const { ModalButtonYellow, ModalButtonBlue, ButtonContainer } =
  MoreModalButtons;

type BookCourseModalProps = {
  visible: boolean;
  onCloseModal: () => void;
  cost: number;
  state: { courseId: number };
  hasPastLessons: boolean;
  numberOfFutureLessons: number;
};

const BookCourseModal = (props: BookCourseModalProps) => {
  const {
    visible,
    onCloseModal,
    cost,
    state,
    hasPastLessons,
    numberOfFutureLessons,
  } = props;

  const { t } = useTranslation("book_a_course");

  const { data: currentUserInfo } = useQuery(GET_CURRENT_USER_INFO, {
    fetchPolicy: "network-only",
  });
  // BOOK COURSE
  const [bookCourse] = useMutation(BOOK_COURSE, {
    refetchQueries: [
      {
        query: GET_MY_CLASSES,
      },
      {
        query: GET_NAVBAR_INFO,
      },
      {
        query: GET_COURSE_DETAILS,
        variables: {
          id: state.courseId,
        },
      },
    ],
    onCompleted: () => {
      message.success(t("Course successfully booked!"));
    },
    onError: (e) => {
      message.error(e.graphQLErrors[0].message);
    },
  });

  const credits = currentUserInfo?.me.credits;
  const handleCreditsMessage = () => {
    if (hasPastLessons)
      return t(
        "To join this ongoing course is a great idea. There are {{ futureClasses }} classes left.",
        {
          futureClasses: numberOfFutureLessons,
          count: numberOfFutureLessons,
        }
      ).concat(
        t(" To book all of them costs {{ cost }} credits.", {
          cost,
          count: cost,
        })
      );
    return t("To book the full course costs {{ cost }} credits.", {
      cost,
      count: cost,
    });
  };

  return (
    <LargeModal
      title={t("BOOK THIS COURSE")}
      centered
      visible={visible}
      footer={null}
      onCancel={onCloseModal}
    >
      <ConfirmationText>
        {t("I want to book all future classes of this course.")}
      </ConfirmationText>
      <ModalRow gutter={24}>
        <ButtonContainer xs={20} sm={20} md={12}>
          <ModalButtonYellow
            type="primary"
            onClick={() => {
              bookCourse({
                variables: { courseId: state.courseId },
              });
              onCloseModal();
            }}
          >
            {t("CONFIRM")}
          </ModalButtonYellow>
        </ButtonContainer>
        <ButtonContainer xs={20} sm={20} md={12}>
          <ModalButtonBlue
            type="primary"
            onClick={() => {
              onCloseModal();
            }}
          >
            {t("CANCEL")}
          </ModalButtonBlue>
        </ButtonContainer>
      </ModalRow>
      <LargeModalCreditInfo>
        <span>
          {handleCreditsMessage()}{" "}
          {t("You currently have {{ credits }} credit left", {
            credits,
            count: credits,
          })}
        </span>
        {credits < cost ? (
          <CreditWarning>
            {t("You have insufficient credits.")}{" "}
            <Link to="/my-credits" style={{ textDecoration: "underline" }}>
              {t("Buy now")} &gt;
            </Link>
          </CreditWarning>
        ) : null}
        <span>
          {t(
            "You can cancel up to {{ hour }} hours before this class starts.",
            { hour: 24 }
          )}
        </span>
      </LargeModalCreditInfo>
    </LargeModal>
  );
};

export default BookCourseModal;
